var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
export class OptomaWuProjector {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.framework = framework;
        // other class properties and constructor
        this.initializerSteps = [
            {
                errorCode: 'verifyFailed',
                send: '~00124 1',
                next: /OK\d+|P|F/i,
            },
        ];
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // The video input query returns different values than the values used to set the input source.
        // This table maps the reported values to the sent values.
        this.videoInputCodes = {
            '1': '2',
            '2': '5',
            '3': '6',
            '4': '9',
            '5': '10',
            '6': '4',
            '7': '1',
            '8': '15',
            '9': '16',
            '10': '11',
            '11': '14',
            '12': '17',
            '13': '18',
            '14': '19',
            '15': '20',
            '16': '21',
            '17': '23',
            '18': '22',
            '20': '24',
        };
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~00355 1`, /OK(\d+)|\>?\s*F/i, tags);
                return match[1] ? match[1] === '1' : this.state.displayBlank.value;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~0002 ${blank ? '1' : '0'}`, /(P|F)/i, tags);
                return match[1].toLowerCase() === 'p';
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const match = yield this.socket.send(`~00122 1`, /Ok(.+)|\>?\s*F/i, tags);
                return (_a = match[1]) !== null && _a !== void 0 ? _a : 'unknown';
            }),
            hasControl: (config) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~00122 1`, /Ok(.+)|\>?\s*F/i);
                return match[1] !== undefined;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~00124 1`, /Ok(\d+)|\>?\s*F/i, tags);
                return match[1] ? match[1] === '1' : this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~0000 ${power ? '1' : '0'}`, /\>?\s*(P|F)/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), power ? 22000 : 10000);
                if (match[1].toLowerCase() === 'p') {
                    // When powering on or off, the projector becomes unresponsive for a while and can
                    // report the old power status. Wait until we receive a response indicating that the
                    // power matches what we set it to.
                    this.framework.suspendPolling(40000);
                    let powerSettled = false;
                    const timeoutMs = Date.now() + 40000;
                    while (!powerSettled && timeoutMs > Date.now()) {
                        try {
                            const pmatch = yield this.socket.send(`~00124 1`, /Ok(\d+)|\>?\s*F/i, [
                                ...tags,
                                'get.power',
                            ]);
                            powerSettled = pmatch[1] === (power ? '1' : '0');
                        }
                        catch (_b) { }
                    }
                    this.framework.resume();
                }
                return match[1].toLowerCase() === 'p' ? power : this.state.power.value;
            }),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~00121 1`, /Ok(\d+)|\>?\s*F/i, tags);
                if (match[1] && match[1] in this.videoInputCodes)
                    return this.videoInputCodes[match[1]];
                return this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~0012 ${code}`, /\>?\s*(P|F)/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return match[1].toLowerCase() === 'p' ? code : this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                // There does not seem to be a volume query
                return this.state.volume.value;
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~0081 ${volume}`, /\>?\s*(P|F)/i, tags);
                return match[1].toLowerCase() === 'p' ? volume : this.state.volume.value;
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 10, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~00356 1`, /Ok(\d+)|\>?\s*F/i, tags);
                return match[1] ? match[1] === '1' : this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`~0080 ${mute ? '1' : '0'}`, /\>?\s*(P|F)/i, tags);
                return match[1].toLowerCase() === 'p' ? mute : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 2000, controls: ['displayBlank', 'power', 'videoInput'] },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield CommandSocketInitializer.initialize(this.socket, this.initializerSteps, credentials);
        });
    }
}
// static properties
OptomaWuProjector.driverType = 'socketDriver';
OptomaWuProjector.key = '-MveI-93xmg0nYNWO0cO';
OptomaWuProjector.baseSocketOptions = {
    sendTimeoutMs: 1500,
};
OptomaWuProjector.socketOptions = [
    Object.assign(Object.assign({}, OptomaWuProjector.baseSocketOptions), { port: 4999 }),
    Object.assign(Object.assign({}, OptomaWuProjector.baseSocketOptions), { port: 23 }),
];
